/**
 * PeriodCompareDelta — tiny coloured pill showing period-over-period change
 * (IA §1.4). Rendered below every KPI value on the Overview tab.
 *
 * Sign convention:
 *   - positive delta (current > previous) → ↑ arrow
 *   - negative delta (current < previous) → ↓ arrow
 *   - |delta| ≤ 0.05% (default) → neutral em-dash, no arrow (Overview P2-2)
 *   - null → muted em-dash with "No comparison available" tooltip
 *
 * `inverted` flips the colour mapping for KPIs where higher is worse
 * (cost, error rate, latency). A 12% jump in cost is red; a 12% jump in
 * active users is green.
 *
 * ### Small-sample guards (Briefs P1-2)
 *
 * When the previous window is tiny (e.g. 24 briefs in the opening days of
 * history vs. 3330 today), the naïve ratio explodes to +13,817%. That's
 * noise dressed as insight. Callers pass `current` / `previous` (absolute
 * values) and we gate the numeric delta on `previous >= minPreviousSampleForDelta`
 * (default: 10). Below that threshold we show a compact "new" pill so the
 * reader knows the metric is growing from a tiny baseline.
 *
 * Edge shapes:
 *   - previous = 0 && current > 0  → "new" pill (ratio is ∞ / NaN).
 *   - current = 0 && previous > 0  → "-100%" pill; the tooltip surfaces
 *     the previous value so the absolute scale is obvious.
 *   - |delta| > 9.99  (|pct| > 999) → "+999%+" / "-999%-" with a trailing
 *     sign so the reader knows the value is truncated.
 *
 * ### Confidence muting (Quality P2-4)
 *
 * When `sampleSize < 3` we grayscale the pill and append an `n=N` badge so
 * readers don't overweight a delta computed on a handful of ratings.
 *
 * ### TODO(audit): OperationsPage, BriefsPage, QualityPage, and OverviewPage
 * should pass `current` + `previous` (raw values, not the ratio) so the
 * tooltip surfaces absolute scale. QualityPage should also pass `sampleSize`
 * on the rating KPIs to trigger the muted-pill / n=N badge.
 */

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { formatRating } from '@/lib/format';
import { cn } from '@/lib/utils';

/**
 * Display format for the raw current/previous values in the tooltip.
 *
 * Mirrors `KpiFormat` in `KPICard.tsx` so every KPI KPICard forwards its own
 * `format` to the delta pill — the tooltip unit always matches the visible
 * value (no more "Time saved: 2.8h" up top and a unit-less "2.80 (was 1.50)"
 * in the hover pill).
 */
export type PeriodCompareFormat =
  | 'number'
  | 'rating'
  | 'percent'
  | 'hours'
  | 'currency';

export interface PeriodCompareDeltaProps {
  /** (current - previous) / previous ratio — may be any finite number. null = n/a. */
  delta: number | null;
  /** If true, positive delta is bad (red). Use for cost/errors. */
  inverted?: boolean;
  /**
   * Tooltip label prefix. Defaults to "vs prev period" which reads as
   * meaningless on hover (the user already knows which KPI they're hovering).
   * KPICards thread their own `label` prop here so the tooltip reads e.g.
   * "Time saved: 2.8h (was 1.5h)" instead of "vs prev period: 2.80 (was 1.50)".
   */
  label?: string;
  /** Optional raw values surfaced in the tooltip. */
  current?: number | null;
  previous?: number | null;
  /**
   * Format applied to `current` + `previous` in the tooltip body — matches
   * the KPI's visible value so the unit never disappears on hover. Omit for
   * bare numeric rendering (preserves legacy callers). See
   * `PeriodCompareFormat` for the enum.
   */
  format?: PeriodCompareFormat;
  /**
   * Minimum previous-period value required before rendering a numeric delta.
   * When `previous < minPreviousSampleForDelta`, we render a "new" pill
   * (previous near zero) or suppress the ratio entirely (previous too small
   * for a reliable comparison). Default 10 for counts; pass e.g. 3 for
   * rating-style fractional metrics.
   */
  minPreviousSampleForDelta?: number;
  /**
   * Current-period sample size. When < 3 the pill is grayscale-muted and an
   * `n=N` badge is appended — prevents +35% on n=2 from reading with the
   * same visual weight as +35% on n=200.
   */
  sampleSize?: number;
}

/** Colour bucket derived from sign + inverted flag + near-zero threshold. */
function deltaColor(
  delta: number,
  inverted: boolean,
): 'green' | 'red' | 'neutral' {
  // 0.0005 ≈ 0.05% — sub-perceptible change, render as neutral em-dash.
  if (Math.abs(delta) < 0.0005) return 'neutral';
  const positive = delta > 0;
  // Non-inverted: positive=good=green, negative=bad=red.
  // Inverted: positive=bad=red, negative=good=green.
  if (positive) return inverted ? 'red' : 'green';
  return inverted ? 'green' : 'red';
}

const COLOR_CLASS: Record<'green' | 'red' | 'neutral', string> = {
  green: 'text-green-600 bg-green-50 border-green-200',
  red: 'text-red-600 bg-red-50 border-red-200',
  neutral: 'text-gray-500 bg-gray-50 border-gray-200',
};

/** Muted overlay applied when sampleSize < 3 — drops saturation so the pill
 *  reads as "directional, not reliable". */
const MUTED_OVERRIDE =
  'text-gray-500 bg-gray-50 border-gray-200 opacity-70 grayscale';

/** Cap at ±999% — anything beyond is noise. Trailing sign marks truncation. */
const PCT_CAP = 999;

/** Format as whole-percent with explicit sign: 0.12 → "+12%", -0.03 → "-3%".
 *  Values outside ±999% are clamped and get a trailing sign marker. */
function formatPct(delta: number): string {
  const rawPct = Math.round(delta * 100);
  if (rawPct > PCT_CAP) return `+${PCT_CAP}%+`;
  if (rawPct < -PCT_CAP) return `-${PCT_CAP}%-`;
  if (rawPct > 0) return `+${rawPct}%`;
  if (rawPct < 0) return `${rawPct}%`;
  return '0%';
}

/** Intl formatters — memoized at module scope so we don't re-allocate. */
const NUMBER_FMT = new Intl.NumberFormat('en-US');
const CURRENCY_FMT = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});

/**
 * Format a raw value for the tooltip. Threaded with a `format` prop so the
 * unit matches the KPI's visible value (no more unit-less "2.80 (was 1.50)"
 * on a Time-saved KPI that renders "2.8h" up top).
 */
function formatRaw(v: number, format?: PeriodCompareFormat): string {
  switch (format) {
    case 'hours':
      // 1 decimal matches KPICard's `formatValue` ("2.8h") so the pill tooltip
      // agrees with the headline to the last digit.
      return `${v.toFixed(1)}h`;
    case 'rating':
      return formatRating(v);
    case 'percent':
      return `${v.toFixed(1)}%`;
    case 'currency':
      return CURRENCY_FMT.format(v);
    case 'number':
      return NUMBER_FMT.format(v);
    default:
      // Legacy bare-number fallback — keeps ad-hoc callers that pass no
      // format hint working. Integers stay as-is; floats round to 2 dp.
      if (Number.isInteger(v)) return String(v);
      return v.toFixed(2);
  }
}

export function PeriodCompareDelta({
  delta,
  inverted = false,
  label = 'vs prev period',
  current,
  previous,
  format,
  minPreviousSampleForDelta = 10,
  sampleSize,
}: PeriodCompareDeltaProps): React.ReactElement {
  // Low-sample-size mute flag — reused across every render branch below.
  const muted = typeof sampleSize === 'number' && sampleSize < 3;
  const nBadge = muted ? ` n=${sampleSize}` : '';

  // "new" pill: previous is exactly zero but current exists — this is a
  // true first-appearance signal, so the blue "new" tag reads correctly.
  // Briefs / Operations audit: when `prev > 0 && prev < threshold` the old
  // code also rendered "new", which looked like a product-tour annotation
  // ("Skipped new", "Cost new"). Those cases now render a muted em-dash
  // pill — the reader sees "no delta yet" without the tag misreading as a
  // feature chip.
  const prevIsZero = previous === 0;
  const prevBelowThreshold =
    typeof previous === 'number' && previous > 0 && previous < minPreviousSampleForDelta;
  const currentExists = typeof current === 'number' && current > 0;
  if (prevIsZero && currentExists) {
    const tooltipBody = `${label}: new (was 0)`;
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              data-testid="period-compare-delta"
              data-variant="new"
              className={cn(
                'inline-flex items-center rounded border px-1.5 py-0.5 text-xs font-medium',
                'text-blue-700 bg-blue-50 border-blue-200',
              )}
            >
              new{nBadge}
            </span>
          </TooltipTrigger>
          <TooltipContent>{tooltipBody}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  if (prevBelowThreshold && currentExists) {
    // Below reliable-comparison threshold — render a muted em-dash pill so
    // the reader understands the delta is withheld without the old blue
    // "new" tag mis-reading as a feature annotation (Briefs P3/Ops audit).
    const tooltipBody = `${label}: ${formatRaw(current, format)} (was ${formatRaw(previous as number, format)} — below reliable-comparison threshold of ${minPreviousSampleForDelta})`;
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              data-testid="period-compare-delta"
              data-variant="low-sample"
              className="inline-flex items-center rounded border px-1.5 py-0.5 text-xs font-medium text-gray-400 bg-gray-50 border-gray-200"
            >
              —{nBadge}
            </span>
          </TooltipTrigger>
          <TooltipContent>{tooltipBody}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // null delta — comparison unavailable (e.g. prev period had 0 for a count
  // metric and current also 0, or the API returned null). Render a muted
  // em-dash with explanatory tooltip so the reader understands the metric
  // has no comparison baseline.
  if (delta === null) {
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              data-testid="period-compare-delta"
              data-variant="null"
              className="inline-flex items-center rounded border px-1.5 py-0.5 text-xs font-medium text-gray-400 bg-gray-50 border-gray-200"
            >
              —{nBadge}
            </span>
          </TooltipTrigger>
          <TooltipContent>No comparison available</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  const color = deltaColor(delta, inverted);

  // Neutral (|delta| < 0.05%) renders as em-dash — avoids the "→ 0%" shape
  // that looked like a typo in Overview P2-2.
  if (color === 'neutral') {
    const tooltipBody =
      current !== undefined && previous !== undefined && current !== null && previous !== null
        ? `${label}: ${formatRaw(current, format)} (was ${formatRaw(previous, format)})`
        : label;
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              data-testid="period-compare-delta"
              data-variant="neutral"
              data-inverted={inverted ? 'true' : 'false'}
              className={cn(
                'inline-flex items-center gap-0.5 rounded border px-1.5 py-0.5 text-xs font-medium',
                muted ? MUTED_OVERRIDE : COLOR_CLASS.neutral,
              )}
            >
              <span>—{nBadge}</span>
            </span>
          </TooltipTrigger>
          <TooltipContent>{tooltipBody}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  const arrow = delta > 0 ? '↑' : '↓';

  const tooltipBody =
    current !== undefined && previous !== undefined && current !== null && previous !== null
      ? `${label}: ${formatRaw(current, format)} (was ${formatRaw(previous, format)})`
      : label;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            data-testid="period-compare-delta"
            data-variant={color}
            data-inverted={inverted ? 'true' : 'false'}
            data-muted={muted ? 'true' : 'false'}
            className={cn(
              'inline-flex items-center gap-0.5 rounded border px-1.5 py-0.5 text-xs font-medium',
              muted ? MUTED_OVERRIDE : COLOR_CLASS[color],
            )}
          >
            <span aria-hidden="true">{arrow}</span>
            <span>
              {formatPct(delta)}
              {nBadge}
            </span>
          </span>
        </TooltipTrigger>
        <TooltipContent>{tooltipBody}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
